/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { catchError, filter, map, mergeMap, skip, switchMap, tap } from 'rxjs/operators';
import {
    createMediaConnection,
    mediaEffectsInitialized,
    selectedAlertOutputDevice,
    selectedAlertOutputDeviceFailed,
    selectedIrrOutputDevice,
    selectedIrrOutputDeviceFailed,
    selectedUserInputDevice,
    selectedUserInputDeviceFailed,
    selectedUserOutputDevice,
    selectedUserOutputDeviceFailed,
    setAlertVolume,
    setCallVolume,
    setIrrVolume,
    setSipDebug,
    tearDownMedia,
    tearDownMediaFailure,
    tearDownMediaSuccess,
    updateMediaDevices
} from './media.actions';
import { of } from 'rxjs';
import * as JsSIP from 'jssip';
import { Action, Store } from '@ngrx/store';
import { MediaService } from '../services/media.service';
import { updateAudioSettings } from '../../settings/+state/settings.actions';
import {
    selectAudioSettings,
    selectPreferredAlertDevice,
    selectPreferredInputDevice,
    selectPreferredIrrDevice,
    selectPreferredOutputDevice
} from '../../settings/+state/settings.selectors';
import { DefaultAudioService } from '../services/default-audio.service';
import { NetworkAlertService } from '../services/network-alert.service';
import { fetchCtcConfigurationSuccess, sipPeerPasswordRequest, sipPeerRequest } from '../../configuration/+state/configuration.actions';
import { MediaConnection } from '../../core/model/media-connection';
import { alertToDevice, alertToHeadsets, irrToDevice, irrToHeadsets, setPeripheralVolume } from './cchub.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { alertToUsbHeadsets, irrToUsbHeadsets, usbAlertToDevice, usbIrrToDevice } from '../../usb/+state/usb.actions';

@Injectable()
export class MediaEffects implements OnInitEffects {
    constructor(
        private actions$: Actions,
        private store: Store,
        private mediaService: MediaService,
        private networkAlertService: NetworkAlertService,
        private defaultAudioService: DefaultAudioService
    ) {}

    ngrxOnInitEffects(): Action {
        return mediaEffectsInitialized();
    }

    createMediaConnections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchCtcConfigurationSuccess),
            mergeMap(({ config }) =>
                config.clusters.map((cluster) => createMediaConnection({ mediaConnection: ({...cluster, socket: MediaService.getSocketUrl(cluster) } as MediaConnection) }))
            )
        )
    );

    initializeMediaConnection$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createMediaConnection),
            mergeMap(({ mediaConnection }) =>
                [sipPeerRequest( { clusterName: mediaConnection.name } ), sipPeerPasswordRequest({ clusterName: mediaConnection.name })]
            )
        )
    );

    setPreferredInput$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMediaDevices),
            concatLatestFrom(() => this.store.select(selectPreferredInputDevice)),
            map(([{ devices }, preferredDeviceId]) =>
                selectedUserInputDevice({ deviceId: MediaService.computeSelectedDeviceId('audioinput', devices, preferredDeviceId, MediaService.mediaDeviceCommunicationsSort) })
            ),
            catchError((err: Error) => of(selectedUserInputDeviceFailed({ payload: err.message })))
        )
    );

    setPreferredOutput$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMediaDevices),
            concatLatestFrom(() => this.store.select(selectPreferredOutputDevice)),
            map(([{ devices }, preferredDeviceId]) =>
                selectedUserOutputDevice({ deviceId: MediaService.computeSelectedDeviceId('audiooutput', devices, preferredDeviceId, MediaService.mediaDevicePrioritySort) })
            ),
            catchError((err: Error) => of(selectedUserOutputDeviceFailed({ payload: err.message })))
        )
    );

    setPreferredAlertOutput$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateMediaDevices),
            concatLatestFrom(() => this.store.select(selectPreferredAlertDevice)),
            map(([{ devices }, preferredDeviceId]) =>
                selectedAlertOutputDevice({ deviceId: MediaService.computeSelectedDeviceId('audiooutput', devices, preferredDeviceId, MediaService.mediaDeviceDefaultSort) })
            ),
            catchError((err: Error) => of(selectedAlertOutputDeviceFailed({ payload: err.message })))
        )
    );

    setPreferredIrrOutput$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(updateMediaDevices),
            concatLatestFrom(() => this.store.select(selectPreferredIrrDevice)),
            map(([{ devices }, preferredDeviceId]) =>
                selectedIrrOutputDevice({ deviceId: MediaService.computeSelectedDeviceId('audiooutput', devices, preferredDeviceId, MediaService.mediaDeviceDefaultSort) })
            ),
            catchError((err: Error) => of(selectedIrrOutputDeviceFailed({ payload: err.message })))
        );
    });

    initializeSipDebug$  = createEffect(() =>
        this.actions$.pipe(
            ofType(mediaEffectsInitialized),
            map(() => setSipDebug({ enable: Boolean(JsSIP.debug.disable()) })),
        )
    );

    toggleSipDebug$ = createEffect(() =>
            this.actions$.pipe(
                ofType(setSipDebug),
                tap(({ enable }) => (enable ? JsSIP.debug.enable('JsSIP:*') : JsSIP.debug.disable()))
            ),
        { dispatch: false }
    );

    saveInputDevice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(selectedUserInputDevice),
            skip(1),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ deviceId }, audioSettings]) => deviceId !== audioSettings.mainInputDeviceId),
            map(() => updateAudioSettings())
        )
    );

    saveOutputDevice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(selectedUserOutputDevice),
            skip(1),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ deviceId }, audioSettings]) => deviceId !== audioSettings.mainOutputDeviceId),
            map(() => updateAudioSettings())
        )
    );

    saveAlertOutputDevice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(selectedAlertOutputDevice),
            skip(1),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ deviceId }, audioSettings]) => deviceId !== audioSettings.alertOutputDeviceId),
            map(() => updateAudioSettings())
        )
    );

    saveIRROutputDevice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(selectedIrrOutputDevice),
            skip(1),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ deviceId }, audioSettings]) => deviceId !== audioSettings.irrOutputDeviceId),
            map(() => updateAudioSettings())
        );
    });

    saveCallVolume$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(setCallVolume),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ volume }, audioSettings]) => volume !== audioSettings.callOutputVolume),
            map(() => updateAudioSettings())
        );
    });
    saveAlertVolume$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(setAlertVolume),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ volume }, audioSettings]) => volume !== audioSettings.alertOutputVolume),
            map(() => updateAudioSettings())
        );
    });
    saveIRRVolume$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(setIrrVolume),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ volume }, audioSettings]) => volume !== audioSettings.irrOutputVolume),
            map(() => updateAudioSettings())
        );
    });
    saveAlertToDevice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(alertToDevice, usbAlertToDevice),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ alert }, audioSettings]) => alert !== audioSettings.alertToDevice),
            map(() => updateAudioSettings())
        );
    });
    saveIrrToDevice$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(irrToDevice, usbIrrToDevice),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ alert }, audioSettings]) => alert !== audioSettings.irrToDevice),
            map(() => updateAudioSettings())
        );
    });
    saveAlertToHeadset$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(alertToHeadsets, alertToUsbHeadsets),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ alert }, audioSettings]) => alert !== audioSettings.alertToHeadsets),
            map(() => updateAudioSettings())
        );
    });
    saveIrrToHeadset$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(irrToHeadsets, irrToUsbHeadsets),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ alert }, audioSettings]) => alert !== audioSettings.irrToHeadsets),
            map(() => updateAudioSettings())
        );
    });
    saveVolumeStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(setPeripheralVolume),
            concatLatestFrom(() => this.store.select(selectAudioSettings)),
            filter(([{ peripheral, volume }, audioSettings]) => !audioSettings.volumeStatus || volume !== audioSettings.volumeStatus[peripheral]?.volume),
            map(() =>  updateAudioSettings())
        );
    });

    orphanedSessionTeardown$ = createEffect(() =>
        this.actions$.pipe(
            ofType(tearDownMedia),
            switchMap(({ represented }) =>
                this.mediaService.destroySession().pipe(
                    map(() => tearDownMediaSuccess({ represented })),
                    catchError((err: Error) => of(tearDownMediaFailure()))
                )
            )
        )
    );

}
